/* Localized product-description images
   ------------------------------------------------------------------
   Swaps an <img> in the product description for a language-specific
   variant when one actually exists, e.g.

       8.jpg   ->   8-es.jpg     (on the Spanish storefront)

   Lightweight by design:
   - runs during browser idle time, so it never blocks rendering / LCP
   - description images sit below the fold, so the original load is cheap
   - a HEAD request is a header-only existence check (no image body)
   - the localized file is only ever downloaded when it exists
*/
(function () {
  "use strict";

  // Default language per storefront TLD — the locale served when the URL has
  // NO /xx/ path prefix. Shopify prefixes every non-default locale, e.g.
  // shopsta.de/en/products/... is English while shopsta.de/products/... is
  // German. Keyed on the country TLD so it matches with or without "www."
  // Keep these codes in sync with the uploaded image suffixes.
  var tldDefaultLang = {
    fr: "fr",
    nl: "nl",
    be: "nl", // French served under /fr/ (handled by prefix)
    de: "de",
    it: "it",
    es: "es",
    pl: "pl",
    pt: "pt",
    se: "sv",
    dk: "da",
    no: "no",
    hu: "hu",
    ro: "ro",
    cz: "cs",
    at: "de",
    ch: "de",
    lt: "lt",
    lv: "lv",
    ee: "et",
    gr: "el",
    ie: "en", // English default
    co: "en"  // English default; /hr/ /de/ handled by prefix
  };

  // Locales we recognise as a URL path prefix (Shopify ISO codes, which also
  // match our image suffixes). "en" is included so an English path short-
  // circuits the swap and the original images are shown.
  var KNOWN_LANGS = {
    en: 1, fr: 1, nl: 1, de: 1, it: 1, es: 1, pl: 1, pt: 1, sv: 1, da: 1,
    hu: 1, ro: 1, cs: 1, lt: 1, lv: 1, et: 1, el: 1, hr: 1,
    fi: 1, no: 1, sk: 1, sl: 1, tr: 1
  };

  // Country TLD (last hostname label) — matches with or without "www."
  // so shopsta.nl and www.shopsta.nl both resolve to "nl".
  var hostLabels = window.location.hostname.split(".");
  var tld = hostLabels[hostLabels.length - 1].toLowerCase();

  // First path segment: a locale prefix (/en/, /fr/ …) or a route (/products).
  var firstSeg = (window.location.pathname.split("/")[1] || "")
    .toLowerCase()
    .split("-")[0]; // normalise e.g. en-gb -> en

  // An explicit locale in the path always wins; otherwise the TLD default.
  var lang = KNOWN_LANGS[firstSeg] ? firstSeg : (tldDefaultLang[tld] || "en");

  // English (or unmapped) — show the original images, swap nothing.
  if (lang === "en") return;

  var suffix = "-" + lang;

  // The localized file may have been re-saved in a different format
  // (e.g. original 8.png, uploaded 8-es.jpg). Try the original extension
  // first, then fall back to the other common image formats.
  var EXT_FALLBACKS = ["jpg", "jpeg", "png", "webp"];

  // Try each candidate URL in turn; swap in the first one that exists.
  // Sequential so a same-format match costs a single HEAD request.
  function trySwap(img, candidates, i) {
    if (i >= candidates.length) return; // no localized file found
    fetch(candidates[i], { method: "HEAD" })
      .then(function (res) {
        if (res && res.ok) {
          // Lock the original's dimensions before swapping, so a
          // higher-resolution localized file renders at the same size.
          var w = img.naturalWidth;
          var h = img.naturalHeight;
          if (w && h && !img.hasAttribute("width")) {
            img.setAttribute("width", w);
            img.setAttribute("height", h);
            img.style.height = "auto"; // preserve aspect ratio when responsive
          }
          img.removeAttribute("srcset"); // ensure the new src wins
          img.src = candidates[i];
        } else {
          trySwap(img, candidates, i + 1);
        }
      })
      .catch(function () {
        trySwap(img, candidates, i + 1); // network/CORS/404 — try next
      });
  }

  function localize() {
    var imgs = document.querySelectorAll(".description-content img");
    if (!imgs.length) return;

    Array.prototype.forEach.call(imgs, function (img) {
      var src = img.getAttribute("src");
      if (!src || /^(data|blob):/i.test(src)) return;

      // Split off any query string (?v=…&width=…) before touching the path.
      var q = src.indexOf("?");
      var pathPart = q === -1 ? src : src.slice(0, q);
      var query = q === -1 ? "" : src.slice(q);

      var dot = pathPart.lastIndexOf(".");
      if (dot === -1) return; // no extension — skip

      var base = pathPart.slice(0, dot);
      var origExt = pathPart.slice(dot + 1).toLowerCase();

      // Already pointing at the localized file? leave it alone.
      if (base.slice(-suffix.length) === suffix) return;

      // Source images may carry an explicit English "-en" marker
      // (e.g. xdobo-x-series-1-en.png). Replace it; files with no marker
      // (e.g. xdobo-p2.jpg) just get the suffix appended.
      var localizedBase = base.slice(-3).toLowerCase() === "-en"
        ? base.slice(0, -3) + suffix
        : base + suffix;

      // Build the extension list: original first, then the fallbacks.
      var exts = [origExt];
      for (var j = 0; j < EXT_FALLBACKS.length; j++) {
        if (EXT_FALLBACKS[j] !== origExt) exts.push(EXT_FALLBACKS[j]);
      }

      var candidates = exts.map(function (e) {
        return localizedBase + "." + e + query;
      });

      trySwap(img, candidates, 0);
    });
  }

  var GALLERY_EXTS = ["jpg", "jpeg", "png", "webp"];

  function parseGalleryUrl(url) {
    if (!url || /^(data|blob):/i.test(url)) return null;
    var q = url.indexOf("?");
    var path = q === -1 ? url : url.slice(0, q);
    var m = path.match(/^(.*?)(_(?:\d+x\d*|x\d+))?\.(jpe?g|png|webp|gif)$/i);
    if (!m) return null;
    var base = m[1];
    if (base.slice(-suffix.length).toLowerCase() === suffix) return null;
    if (base.slice(-3).toLowerCase() === "-en") base = base.slice(0, -3);
    return { base: base, size: m[2] || "", ext: m[3].toLowerCase() };
  }

  function localizedFor(base, size, ext) {
    return base + suffix + size + "." + ext;
  }

  function localizeGallery() {
    var root = document.querySelector(".product-gallery") || document;

    var cache = {};
    var pending = {};

    function request(base, origExt, apply) {
      if (cache[base] === false) return;
      if (typeof cache[base] === "string") { apply(cache[base]); return; }
      if (pending[base]) { pending[base].push(apply); return; }
      pending[base] = [apply];
      var exts = [origExt];
      GALLERY_EXTS.forEach(function (e) { if (e !== origExt) exts.push(e); });
      (function tryExt(i) {
        if (i >= exts.length) { cache[base] = false; pending[base] = null; return; }
        fetch(base + suffix + "." + exts[i], { method: "HEAD" })
          .then(function (res) {
            if (res && res.ok) {
              cache[base] = exts[i];
              var fns = pending[base]; pending[base] = null;
              fns.forEach(function (fn) { fn(exts[i]); });
            } else { tryExt(i + 1); }
          })
          .catch(function () { tryExt(i + 1); });
      })(0);
    }

    function swapImg(img) {
      var p = parseGalleryUrl(img.getAttribute("src"));
      if (!p) return;
      request(p.base, p.ext, function (ext) {
        img.setAttribute("src", localizedFor(p.base, p.size, ext));
        var ss = img.getAttribute("srcset");
        if (ss && ss.indexOf("data:") === -1) {
          img.setAttribute("srcset", ss.replace(/[^\s,]+/g, function (tok) {
            if (/^[\d.]+x$/.test(tok) || /^[\d]+w$/.test(tok)) return tok;
            var pp = parseGalleryUrl(tok);
            return pp ? localizedFor(pp.base, pp.size, ext) : tok;
          }));
        }
      });
    }

    function swapZoom(fig) {
      var p = parseGalleryUrl(fig.getAttribute("data-zoom"));
      if (!p) return;
      request(p.base, p.ext, function (ext) {
        fig.setAttribute("data-zoom", localizedFor(p.base, p.size, ext));
      });
    }

    function swapBg(el) {
      var style = el.getAttribute("style") || "";
      var m = style.match(/url\(['"]?([^'")]+)['"]?\)/);
      if (!m) return;
      var p = parseGalleryUrl(m[1]);
      if (!p) return;
      request(p.base, p.ext, function (ext) {
        el.setAttribute("style", style.replace(m[1], localizedFor(p.base, p.size, ext)));
      });
    }

    Array.prototype.forEach.call(root.querySelectorAll("[data-gallery-figure]:not([data-gallery-index='0']) img, [data-gallery-thumbnail]:not([data-gallery-index='0']) .product-gallery--media-thumbnail-img"), swapImg);
    Array.prototype.forEach.call(root.querySelectorAll("[data-gallery-figure]:not([data-gallery-index='0'])[data-zoom]"), swapZoom);
    Array.prototype.forEach.call(root.querySelectorAll("[data-gallery-figure]:not([data-gallery-index='0']) .product-gallery--image-background"), swapBg);
  }

  function start() {
    localizeGallery();
    setTimeout(localizeGallery, 400);
    setTimeout(localizeGallery, 1500);
    if (window.requestIdleCallback) {
      window.requestIdleCallback(localize, { timeout: 2000 });
    } else {
      setTimeout(localize, 200);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
